import React, { useLayoutEffect } from 'react';
import DemoBooking from './demo-booking';
import { startPublicAnalytics, stopPublicMeasurement } from './public-analytics';

const styles = `
/* index.html offsets the app shell by the fixed AppBar height (57px);
   break out of it so the landing page is full-bleed */
body { overflow-x: hidden; }
.lp {
    position: relative;
    width: 100vw;
    left: 50%;
    margin-left: -50vw;
    margin-top: -57px;
}
.lp {
    /* Aliases of the --ds-* design-system tokens — see docs/design-system.md */
    --lp-blue: #1a5fb4;
    --lp-blue-hover: #174f96;
    --lp-blue-dark: #123c72;
    --lp-blue-deep: #0d2b52;
    --lp-blue-light: #e8f0fb;
    --lp-ink: #1c1e21;
    --lp-gray: #555555;
    --lp-border: #e2e5e9;
    --lp-bg: #f5f6f8;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif;
    color: var(--lp-ink);
    background: var(--lp-bg);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
}
.lp * { box-sizing: border-box; }
.lp h1, .lp h2, .lp h3 { line-height: 1.25; margin: 0; }
.lp p { margin: 0; }
.lp img { max-width: 100%; }
.lp section[id] { scroll-margin-top: 80px; }
.lp-wrap { max-width: 1120px; margin: 0 auto; padding: 0 24px; }

/* ---- nav ---- */
.lp-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(255,255,255,.95);
    border-bottom: 1px solid var(--lp-border);
    backdrop-filter: blur(8px);
}
.lp-nav-inner {
    display: flex; align-items: center; justify-content: space-between;
    height: 64px;
}
.lp-brand { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 17px; }
.lp-brand .material-icons { color: var(--lp-blue); }
.lp-nav-links { display: flex; align-items: center; gap: 24px; }
.lp-nav-links a { color: var(--lp-gray); text-decoration: none; font-size: 14px; font-weight: 500; }
.lp-nav-links a:hover { color: var(--lp-blue); }

/* ---- buttons ---- */
.lp-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 24px; border-radius: 8px;
    font-size: 15px; font-weight: 600; text-decoration: none;
    transition: all .15s ease; border: none; cursor: pointer;
}
.lp-btn:hover { text-decoration: none; }
.lp-btn-primary { background: var(--lp-blue); color: #fff !important; }
.lp-btn-primary:hover { background: var(--lp-blue-hover); color: #fff; }
.lp-btn-ghost { background: #fff; color: var(--lp-blue) !important; border: 1px solid var(--lp-border); }
.lp-btn-ghost:hover { border-color: var(--lp-blue); color: var(--lp-blue); }
.lp-btn-sm { padding: 8px 18px; font-size: 14px; }
.lp-btn-hero-white { background: #fff; color: var(--lp-blue) !important; }
.lp-btn-hero-white:hover { background: #e8f0fb; color: var(--lp-blue-dark) !important; }
.lp-btn-hero-outline { background: transparent; color: #fff !important; border: 1px solid rgba(255,255,255,.45); }
.lp-btn-hero-outline:hover { border-color: #fff; background: rgba(255,255,255,.08); }

/* ---- hero ---- */
.lp-hero {
    background: linear-gradient(160deg, var(--lp-blue-dark) 0%, var(--lp-blue) 55%, #3576cf 100%);
    color: #fff;
    padding: 88px 0 96px;
}
.lp-hero-grid { display: flex; align-items: center; gap: 56px; }
.lp-hero-text { flex: 1 1 460px; }
.lp-hero-shot { flex: 1 1 420px; min-width: 0; }
.lp-kicker {
    display: inline-block; padding: 6px 14px; border-radius: 999px;
    background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.25);
    font-size: 13px; font-weight: 600; letter-spacing: .4px; margin-bottom: 20px;
}
.lp-hero h1 { font-size: 42px; font-weight: 800; letter-spacing: -.5px; }
.lp-hero-sub { margin-top: 18px; font-size: 18px; color: #d6e4f5; max-width: 540px; }
.lp .lp-hero-audience { margin-top: 16px; font-size: 14px; color: #d6e4f5; max-width: 540px; }
.lp-hero-cta { margin-top: 32px; display: flex; gap: 14px; flex-wrap: wrap; }
.lp-hero-points { margin-top: 28px; display: flex; gap: 22px; flex-wrap: wrap; }
.lp-hero-point { display: flex; align-items: center; gap: 7px; font-size: 14px; color: #d6e4f5; }
.lp-hero-point .material-icons { font-size: 18px; color: #9dc0ea; }
.lp-browser {
    background: #fff; border-radius: 12px; overflow: hidden;
    box-shadow: 0 24px 60px rgba(13, 43, 82, .45);
}
.lp-browser-bar { display: flex; gap: 6px; padding: 10px 14px; background: #f5f6f8; border-bottom: 1px solid var(--lp-border); }
.lp-browser-bar span { width: 10px; height: 10px; border-radius: 50%; background: #c6d3e3; }
.lp-browser img { display: block; width: 100%; }

/* ---- sections ---- */
.lp-section { padding: 80px 0; }
.lp-section-alt { background: #fff; border-top: 1px solid var(--lp-border); border-bottom: 1px solid var(--lp-border); }
.lp-eyebrow { color: var(--lp-blue); font-weight: 700; font-size: 13px; letter-spacing: 1.2px; text-transform: uppercase; }
.lp-h2 { font-size: 32px; font-weight: 800; margin-top: 10px; letter-spacing: -.3px; }
.lp-lead { margin-top: 14px; font-size: 17px; color: var(--lp-gray); max-width: 640px; }
.lp-center { text-align: center; }
.lp-center .lp-lead { margin-left: auto; margin-right: auto; }

/* ---- features ---- */
.lp-features {
    margin-top: 48px; display: grid; gap: 20px;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}
.lp-feature {
    background: #fff; border: 1px solid var(--lp-border); border-radius: 12px;
    padding: 26px; text-align: left; transition: box-shadow .15s ease;
}
.lp-feature:hover { box-shadow: 0 10px 30px rgba(28, 30, 33, .08); }
.lp-feature-icon {
    width: 46px; height: 46px; border-radius: 10px;
    background: var(--lp-blue-light); color: var(--lp-blue);
    display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
}
.lp-feature h3 { font-size: 17px; font-weight: 700; }
.lp-feature p { margin-top: 8px; font-size: 14.5px; color: var(--lp-gray); }

/* ---- signing highlight ---- */
.lp-sign {
    background: linear-gradient(150deg, var(--lp-blue-deep) 0%, var(--lp-blue-dark) 100%);
    border-radius: 18px; color: #fff; overflow: hidden;
    display: flex; align-items: stretch; gap: 0;
}
.lp-sign-body { flex: 1 1 520px; padding: 52px; }
.lp-sign-side {
    flex: 1 1 320px; min-width: 280px;
    background: rgba(255,255,255,.06); border-left: 1px solid rgba(255,255,255,.12);
    display: flex; flex-direction: column; justify-content: center; padding: 44px;
}
.lp-badge-new {
    display: inline-block; background: #1a7f37; color: #ffffff;
    font-size: 12px; font-weight: 800; letter-spacing: 1px;
    padding: 4px 12px; border-radius: 999px; margin-bottom: 16px;
}
.lp-sign h2 { font-size: 30px; font-weight: 800; letter-spacing: -.3px; }
.lp-sign-lead { margin-top: 14px; color: #c6d3e3; font-size: 16.5px; max-width: 560px; }
.lp-sign-list { margin: 26px 0 0; padding: 0; list-style: none; }
.lp-sign-list li { display: flex; align-items: flex-start; gap: 12px; margin-top: 14px; font-size: 15px; color: #dfe8f3; }
.lp-sign-list .material-icons { font-size: 20px; color: #5fbf77; margin-top: 1px; }
.lp-sign-side .material-icons.lp-sign-big { font-size: 72px; color: #6f9fd8; }
.lp-sign-side h3 { margin-top: 18px; font-size: 18px; font-weight: 700; }
.lp-sign-side p { margin-top: 8px; font-size: 14px; color: #c6d3e3; }
.lp-os { margin-top: 18px; display: flex; gap: 10px; flex-wrap: wrap; }
.lp-os span {
    font-size: 12.5px; font-weight: 600; padding: 5px 12px; border-radius: 999px;
    background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
}

/* ---- steps ---- */
.lp-steps { margin-top: 48px; display: grid; gap: 20px; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); }
.lp-step { background: #fff; border: 1px solid var(--lp-border); border-radius: 12px; padding: 26px; }
.lp-step-num {
    width: 34px; height: 34px; border-radius: 50%;
    background: var(--lp-blue); color: #fff; font-weight: 700; font-size: 15px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
}
.lp-step h3 { font-size: 16px; font-weight: 700; }
.lp-step p { margin-top: 8px; font-size: 14px; color: var(--lp-gray); }

/* ---- screenshots ---- */
.lp-shots { margin-top: 56px; display: flex; flex-direction: column; gap: 72px; }
.lp-shot { display: flex; align-items: center; gap: 48px; }
.lp-shot:nth-child(even) { flex-direction: row-reverse; }
.lp-shot-text { flex: 1 1 340px; }
.lp-shot-img { flex: 1 1 480px; min-width: 0; }
.lp-shot-img img {
    display: block; width: 100%; border-radius: 12px;
    border: 1px solid var(--lp-border);
    box-shadow: 0 16px 40px rgba(28, 30, 33, .1);
}
.lp-shot-text h3 { font-size: 22px; font-weight: 800; }
.lp-shot-text p { margin-top: 12px; font-size: 15.5px; color: var(--lp-gray); }

/* ---- trust ---- */
.lp-trust { text-align: center; }
.lp-trust-logos { margin-top: 36px; display: flex; justify-content: center; align-items: center; gap: 48px; flex-wrap: wrap; }
.lp-trust-logos img { max-height: 120px; }

/* ---- contact ---- */
.lp-contact-grid { margin-top: 40px; display: grid; gap: 20px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
.lp-contact-card {
    background: #fff; border: 1px solid var(--lp-border); border-radius: 12px;
    padding: 28px; display: flex; align-items: center; gap: 16px;
}
.lp-contact-card .material-icons { color: var(--lp-blue); font-size: 30px; }
.lp-contact-card small { display: block; color: var(--lp-gray); font-size: 13px; }
.lp-contact-card strong { font-size: 17px; overflow-wrap: anywhere; }
.lp-contact-card > div { min-width: 0; }
.lp-contact-card a { color: inherit; text-decoration: none; }

/* ---- footer ---- */
.lp-footer { border-top: 1px solid var(--lp-border); background: #fff; padding: 28px 0; }
.lp-footer-inner { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; font-size: 14px; color: var(--lp-gray); }

@media (max-width: 900px) {
    .lp-hero { padding: 56px 0 64px; }
    .lp-hero h1 { font-size: 32px; }
    .lp-hero-grid { flex-direction: column; }
    .lp-hero-text, .lp-hero-shot { flex: 0 1 auto; width: 100%; }
    .lp-sign { flex-direction: column; }
    .lp-sign-body { padding: 36px 28px; }
    .lp-sign-side { border-left: none; border-top: 1px solid rgba(255,255,255,.12); padding: 32px 28px; }
    .lp-shot, .lp-shot:nth-child(even) { flex-direction: column; }
    .lp-nav-links a.lp-nav-hide { display: none; }
    .lp-section { padding: 56px 0; }
    .lp-h2 { font-size: 26px; }
}
`;

const FEATURES = [
    {
        icon: 'description',
        title: 'Referate din toate compartimentele',
        text: 'Fiecare compartiment inițiază referate de necesitate în aceeași aplicație, cu articole din nomenclatorul instituției.'
    },
    {
        icon: 'fact_check',
        title: 'Aprobări între compartimente',
        text: 'Directorul economic, managerul și biroul de achiziții aprobă referatele online, în fluxul instituției.'
    },
    {
        icon: 'list_alt',
        title: 'Starea fiecărui referat, la vedere',
        text: 'Urmăriți referatele și aprobările într-un singur loc. Accesul la documente depinde de rolul fiecărui utilizator.'
    },
    {
        icon: 'verified_user',
        title: 'Semnătură electronică calificată',
        text: 'Referatele se semnează digital cu certificat calificat pe token USB — documente cu valoare legală, fără hârtie.'
    },
    {
        icon: 'assessment',
        title: 'Rapoarte privind achizițiile',
        text: 'Situația achizițiilor pe an sau pe intervale lunare, generată automat, gata de export și raportare.'
    },
    {
        icon: 'tune',
        title: 'Funcționalități la cerere',
        text: 'Discutăm procedurile instituției dumneavoastră și funcționalitățile necesare echipei.'
    }
];

const STEPS = [
    {
        title: 'Creezi referatul',
        text: 'Șeful de departament completează justificarea, categoria de achiziție, sursa de finanțare și articolele necesare.'
    },
    {
        title: 'Aprobare online',
        text: 'Referatul trece prin fluxul de aprobare: director economic, manager și biroul de achiziții publice.'
    },
    {
        title: 'Semnare digitală',
        text: 'Documentul PDF se semnează electronic cu certificat calificat pe token — sau se tipărește clasic, la alegere.'
    },
    {
        title: 'Urmărire și rapoarte',
        text: 'Articolele cumpărate intră automat în situația achizițiilor, cu date contabile și recepție.'
    }
];

const SHOTS = [
    {
        img: './pictures/create_paper.png',
        title: 'Crearea unui referat de necesitate',
        text: 'Orice șef de departament poate crea un referat de necesitate cu justificarea achiziției, categoria, sursa de ' +
            'finanțare și articole selectate din lista instituției. Referatul poate fi trimis spre aprobare sau salvat pentru modificări ulterioare.'
    },
    {
        img: './pictures/approve_1.png',
        title: 'Aprobarea referatelor, fără drumuri între birouri',
        text: 'Persoanele cu rol de aprobare — directorul economic, managerul instituției și reprezentantul biroului de achiziții — ' +
            'aprobă referatele direct din aplicație, de la birou sau de acasă.'
    },
    {
        img: './pictures/see_all.png',
        title: 'Toate referatele și starea lor, într-un singur loc',
        text: 'Persoanele din procesul de aprobare văd toate referatele instituției, iar ceilalți angajați văd doar referatele proprii. ' +
            'Starea fiecărui referat este vizibilă în timp real.'
    },
    {
        img: './pictures/print_paper.png',
        title: 'Documentul final, generat automat',
        text: 'După aprobare, referatul se generează în format oficial — gata de semnat electronic cu token sau de tipărit ' +
            'pentru semnare și ștampilare clasică.'
    },
    {
        img: './pictures/article_details_list.png',
        title: 'Evidența articolelor cumpărate',
        text: 'După aprobare, articolele referatului intră în lista produselor cumpărate. Pentru fiecare produs se pot introduce ' +
            'datele contabile și recepția.'
    },
    {
        img: './pictures/acquisitions_report.png',
        title: 'Situația achizițiilor, generată automat',
        text: 'Raportul privind achizițiile pe un an sau pe intervale lunare se generează automat din datele introduse — ' +
            'fără tabele ținute manual în Excel.'
    }
];

export default function LandingPage() {
    useLayoutEffect(() => {
        startPublicAnalytics();
        return () => stopPublicMeasurement();
    }, []);
    return (
        <div className="lp">
            <style>{styles}</style>

            <nav className="lp-nav">
                <div className="lp-wrap lp-nav-inner">
                    <div className="lp-brand">
                        <i className="material-icons">gavel</i>
                        Achiziții Publice
                    </div>
                    <div className="lp-nav-links">
                        <a className="lp-nav-hide" href="#functionalitati">Funcționalități</a>
                        <a className="lp-nav-hide" href="#semnatura">Semnătură digitală</a>
                        <a className="lp-nav-hide" href="#platforma" data-analytics-event="view_platform_click" data-analytics-label="platform_preview">Platforma</a>
                        <a className="lp-nav-hide" href="#contact" data-analytics-event="book_demo_click" data-analytics-label="demo_navigation">Demo de 30 min</a>
                        <a className="lp-btn lp-btn-primary lp-btn-sm" href="/login">Autentificare</a>
                    </div>
                </div>
            </nav>

            <header className="lp-hero">
                <div className="lp-wrap lp-hero-grid">
                    <div className="lp-hero-text">
                        <span className="lp-kicker">Exclusiv pentru instituții publice din România</span>
                        <h1>Referate de necesitate și aprobări online</h1>
                        <p className="lp-hero-sub">
                            Pentru instituții cu minimum 30 de angajați, în care mai multe compartimente inițiază referate.
                            Centralizați documentele, aprobările și urmărirea stării lor într-o singură aplicație.
                        </p>
                        <p className="lp-hero-audience">
                            Opere, teatre, filarmonici, primării, muzee, biblioteci și alte instituții publice.
                        </p>
                        <div className="lp-hero-cta">
                            <a className="lp-btn lp-btn-hero-white" href="#contact" data-analytics-event="book_demo_click" data-analytics-label="demo_navigation">
                                Solicită un demo de 30 min
                            </a>
                            <a className="lp-btn lp-btn-hero-outline" href="#platforma" data-analytics-event="view_platform_click" data-analytics-label="platform_preview">
                                Vezi platforma
                            </a>
                        </div>
                        <div className="lp-hero-points">
                            <span className="lp-hero-point"><i className="material-icons">check_circle</i> Referate centralizate</span>
                            <span className="lp-hero-point"><i className="material-icons">check_circle</i> Aprobare online</span>
                            <span className="lp-hero-point"><i className="material-icons">check_circle</i> Starea documentelor</span>
                        </div>
                    </div>
                    <div className="lp-hero-shot">
                        <div className="lp-browser">
                            <div className="lp-browser-bar"><span/><span/><span/></div>
                            <img src={'./pictures/see_all.png'} alt="Lista referatelor de necesitate"/>
                        </div>
                    </div>
                </div>
            </header>

            <section className="lp-section" id="functionalitati">
                <div className="lp-wrap lp-center">
                    <span className="lp-eyebrow">Funcționalități</span>
                    <h2 className="lp-h2">De la referatul compartimentului la aprobarea instituției</h2>
                    <p className="lp-lead">
                        Pentru responsabili de achiziții, directori economici, responsabili administrativi și manageri
                        care au nevoie de o evidență comună a referatelor și aprobărilor.
                    </p>
                    <div className="lp-features">
                        {FEATURES.map(f => (
                            <div className="lp-feature" key={f.title}>
                                <div className="lp-feature-icon"><i className="material-icons">{f.icon}</i></div>
                                <h3>{f.title}</h3>
                                <p>{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="lp-section lp-section-alt" id="semnatura">
                <div className="lp-wrap">
                    <div className="lp-sign">
                        <div className="lp-sign-body">
                            <span className="lp-badge-new">NOU</span>
                            <h2>Documente semnate digital, cu certificat calificat pe token</h2>
                            <p className="lp-sign-lead">
                                Referatele aprobate se semnează electronic direct cu token-ul USB al instituției —
                                aceeași semnătură calificată pe care o folosiți deja pentru SEAP sau ANAF.
                            </p>
                            <ul className="lp-sign-list">
                                <li>
                                    <i className="material-icons">check_circle</i>
                                    Semnătură electronică calificată cu certificat pe token USB (SafeNet / DigiSign)
                                </li>
                                <li>
                                    <i className="material-icons">check_circle</i>
                                    Semnătură PAdES vizibilă, aplicată direct pe PDF-ul referatului — validabilă în Adobe Reader
                                </li>
                                <li>
                                    <i className="material-icons">check_circle</i>
                                    Documente cu valoare legală, fără imprimare, semnare pe hârtie și arhivare fizică
                                </li>
                                <li>
                                    <i className="material-icons">check_circle</i>
                                    PIN-ul token-ului rămâne la utilizator — nu este stocat și nu părăsește calculatorul
                                </li>
                            </ul>
                        </div>
                        <div className="lp-sign-side">
                            <i className="material-icons lp-sign-big">usb</i>
                            <h3>Funcționează pe macOS și Windows</h3>
                            <div className="lp-os">
                                <span>Windows</span>
                                <span>macOS</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="lp-section">
                <div className="lp-wrap lp-center">
                    <span className="lp-eyebrow">Cum funcționează</span>
                    <h2 className="lp-h2">De la necesitate la raport, în patru pași</h2>
                    <div className="lp-steps">
                        {STEPS.map((s, i) => (
                            <div className="lp-step" key={s.title}>
                                <div className="lp-step-num">{i + 1}</div>
                                <h3>{s.title}</h3>
                                <p>{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="lp-section lp-section-alt" id="platforma">
                <div className="lp-wrap">
                    <div className="lp-center">
                        <span className="lp-eyebrow">Platforma în acțiune</span>
                        <h2 className="lp-h2">Referatele și aprobările, pas cu pas</h2>
                    </div>
                    <div className="lp-shots">
                        {SHOTS.map(s => (
                            <div className="lp-shot" key={s.title}>
                                <div className="lp-shot-text">
                                    <h3>{s.title}</h3>
                                    <p>{s.text}</p>
                                </div>
                                <div className="lp-shot-img">
                                    <img src={s.img} alt={s.title}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="lp-section">
                <div className="lp-wrap lp-trust">
                    <span className="lp-eyebrow">Referințe</span>
                    <h2 className="lp-h2">Opera Națională Română Cluj-Napoca</h2>
                    <div className="lp-trust-logos">
                        <a href="http://operacluj.ro/" target="_blank" rel="noopener noreferrer">
                            <img src={'./pictures/logo_opera.png'} alt="Opera Națională Română Cluj-Napoca"/>
                        </a>
                    </div>
                </div>
            </section>

            <section className="lp-section lp-section-alt" id="contact">
                <div className="lp-wrap">
                    <div className="lp-center">
                        <span className="lp-eyebrow">Demonstrație online</span>
                        <h2 className="lp-h2">Demonstrație platformă achiziții — 30 minute</h2>
                        <p className="lp-lead">
                            Completați datele instituției și descrieți cum gestionați referatele în prezent.
                            Vom verifica împreună potrivirea platformei și detaliile comerciale înainte de confirmarea demonstrației.
                        </p>
                    </div>
                    <DemoBooking/>
                    <div className="lp-contact-grid">
                        <div className="lp-contact-card">
                            <i className="material-icons">call</i>
                            <div>
                                <small>Ne puteți contacta și prin telefon</small>
                                <strong><a href="tel:+40746490768" data-analytics-event="phone_click" data-analytics-label="sales_phone">0746 490 768</a></strong>
                            </div>
                        </div>
                        <div className="lp-contact-card">
                            <i className="material-icons">mail</i>
                            <div>
                                <small>Sau prin e-mail</small>
                                <strong><a href="mailto:lucian@partsfindersoftware.com" data-analytics-event="email_click" data-analytics-label="sales_email">lucian@partsfindersoftware.com</a></strong>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="lp-footer">
                <div className="lp-wrap lp-footer-inner">
                    <span>© {new Date().getFullYear()} CLP Studio. Toate drepturile rezervate.</span>
                    <span>Referate și aprobări pentru instituții publice</span>
                </div>
            </footer>
        </div>
    );
}
